import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import { Box, CardActionArea, Typography } from '@mui/material';
import NewsCard from './NewsCard';
import { useDevice } from '../../../contexts/DeviceContext';
import { getCurrentLayout } from '../../../utils/ChooseLayout';
import { useResourceLibraryNewsItems } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibraryNewsItemsProvider';
import { useResourceLibraryNewProductIntroductionItems } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibraryNewProductIntroductionItemsProvider';
import { useResourceLibrarySitePageItems } from '@/graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibrarySitePageItemsProvider';
import { ResourceLibraryNewsUnionBaseItem } from '../../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import { getAssetUrl } from '../../../utils/Pimcore';
import { getResourceLibraryItemOpenUrl } from '../../../utils/openResourceLibraryItem';
import type {
    Filters,
    KeyFilters,
} from '../../../graphql/pimcore/modules/resourceLibrary/types/filters';
import {
    convertFilterKeysToFilters,
    filterItemsWithRelevance,
} from '../../../graphql/pimcore/modules/resourceLibrary/contexts/filters';
import { useResourceTypes } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import type { SearchResult } from '../../../graphql/pimcore/modules/resourceLibrary/types/search';

export interface NewsTabProps {
    // The keys of the resource types to filter for, e.g. ['News/Corporate', 'News/Financial']
    filterKeys: string[];
}

// Helper to determine grid size based on layout and item index.
export const getXsValue = (layout: string, index: number) => {
    if (layout === 'medium') {
        return index === 0 ? 12 : 6;
    } else if (layout.includes('small')) {
        return 12;
    } else {
        return index === 0 ? 8 : 4;
    }
};

export default function NewsTab({ filterKeys }: NewsTabProps) {
    const { width } = useDevice();
    const layout = getCurrentLayout(width);
    const { items: newsItems } = useResourceLibraryNewsItems();
    const { items: npiItems } = useResourceLibraryNewProductIntroductionItems();
    const { items: sitePageItems } = useResourceLibrarySitePageItems();
    const { resourceTypeById, resourceTypeByKey } = useResourceTypes();

    const resolvedFilters: Pick<Filters, 'resourceTypeIds'> = useMemo(() => {
        // Build filters from passed keys.
        const filters: Pick<KeyFilters, 'resourceTypeKeys'> = {
            resourceTypeKeys: filterKeys,
        };
        return convertFilterKeysToFilters(filters, { resourceTypeByKey });
    }, [filterKeys, resourceTypeByKey]);

    const filteredItems = useMemo(() => {
        type NewsUnionWithCoverImage = ResourceLibraryNewsUnionBaseItem & {
            coverImageUrl: string;
        };

        const items: ResourceLibraryNewsUnionBaseItem[] = [
            ...newsItems,
            ...npiItems,
            ...sitePageItems,
        ];

        const results = filterItemsWithRelevance<
            Pick<Filters, 'resourceTypeIds'>,
            ResourceLibraryNewsUnionBaseItem
        >({
            items,
            filters: resolvedFilters,
            sortOrder: 'latest',
            filterResolvers: { resourceTypeById },
            computeRelevance: false,
            sortProps: ['publicationDate'],
        });

        const hasCoverImage = (
            item: ResourceLibraryNewsUnionBaseItem
        ): item is NewsUnionWithCoverImage => Boolean(item.coverImageUrl);

        const selectedItems: NewsUnionWithCoverImage[] = [];

        for (const { item } of results) {
            if (!hasCoverImage(item)) {
                console.error(
                    `Item "${item.name}" is missing coverImageUrl and will be excluded from NewsTab.`
                );
                continue;
            }

            selectedItems.push(item);
            if (selectedItems.length === 5) {
                break;
            }
        }

        return selectedItems;
    }, [newsItems, npiItems, resolvedFilters, resourceTypeById, sitePageItems]);

    return (
        <Box sx={{ flexGrow: 2 }} data-testid={'tab-box'}>
            <Grid container spacing={3} data-testid={'grid-container'}>
                {filteredItems.map((newsItem, index) => (
                    <Grid item xs={getXsValue(layout, index)} key={newsItem.id}>
                        {index === 0 ? (
                            <CardActionArea
                                component={RouterLink}
                                to={getResourceLibraryItemOpenUrl({ item: newsItem }) || ''}
                                sx={{ overflow: 'hidden', borderRadius: '0.63rem' }}
                            >
                                <div
                                    className="first-card-image"
                                    style={{
                                        position: 'relative',
                                        height: layout === 'extra_small' ? '16rem' : '20rem',
                                        marginLeft: layout === 'extra_small' ? '0' : '0.5rem',
                                        marginRight: layout === 'extra_small' ? '0' : '0.35rem',
                                    }}
                                >
                                    <img
                                        src={getAssetUrl(newsItem.coverImageUrl) ?? ''}
                                        alt={newsItem.name}
                                        aria-hidden="true"
                                    />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            background:
                                                'linear-gradient(180deg, transparent, rgba(229, 103, 62, 0.7))',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-end',
                                            padding: '1.5rem',
                                            display: 'flex',
                                            borderRadius: '0.63rem',
                                        }}
                                    >
                                        <Typography
                                            color="white"
                                            gutterBottom
                                            fontSize="1.25rem"
                                            fontWeight="700"
                                            lineHeight="1.25rem"
                                            textAlign={layout.includes('large') ? 'left' : 'center'}
                                            width={
                                                layout === 'extra_small' ||
                                                layout === 'small' ||
                                                layout === 'medium'
                                                    ? '100%'
                                                    : '26rem'
                                            }
                                        >
                                            {newsItem.name}
                                        </Typography>
                                    </div>
                                </div>
                            </CardActionArea>
                        ) : (
                            <NewsCard
                                image={getAssetUrl(newsItem.coverImageUrl) ?? ''}
                                title={newsItem.name}
                                url={getResourceLibraryItemOpenUrl({ item: newsItem }) || ''}
                                id={newsItem.id}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}
